/**
 * Single source of truth for the physical world: table geometry, per-variant
 * ball specs, tuned physics constants, render styling, and the standard opening
 * layout. Both the physics engine (via the World it is handed) and the renderer
 * read their dimensions from here so visuals and simulation never diverge.
 *
 * Pure data — no imports, no Three.js, no DOM. Units are SI (metres, kg, s, rad)
 * unless a field name says otherwise.
 */

export type VariantId = 'straight-rail' | 'three-cushion';

export type BallId = 'white' | 'yellow' | 'red' | 'red2';

/** The two cue balls (one per player); the reds are the shared object balls. */
export const CUE_BALL_IDS: readonly BallId[] = ['white', 'yellow'];
/** Superset of every ball id across all variants (for colours / enumeration). */
export const ALL_BALL_IDS: readonly BallId[] = ['white', 'yellow', 'red', 'red2'];

/**
 * Balls actually in play per variant:
 * - three-cushion: 3 balls (white + yellow cues, one red object ball).
 * - straight rail (Korean 4구): 4 balls (white + yellow cues, two red object balls).
 */
export const BALLS_BY_VARIANT: Record<VariantId, readonly BallId[]> = {
  'straight-rail': ['white', 'yellow', 'red', 'red2'],
  'three-cushion': ['white', 'yellow', 'red'],
};

/** The balls in play for a given variant. */
export function ballsFor(variant: VariantId): readonly BallId[] {
  return BALLS_BY_VARIANT[variant];
}

export interface BallSpec {
  /** Ball diameter in metres. */
  diameter: number;
  /** Ball mass in kg. */
  mass: number;
}

export interface Vec3Lit {
  x: number;
  y: number;
  z: number;
}

/**
 * Table geometry. The "play surface" is the rectangle bounded by the cushion
 * noses (where ball centres are constrained to |x| <= playWidth/2 - R, etc.).
 * Coordinate frame: origin at table centre, +x short axis, +y long axis, +z up.
 */
export interface TableSpec {
  /** Short dimension of the play surface (x), metres. */
  playWidth: number;
  /** Long dimension of the play surface (y), metres. 2:1 vs playWidth. */
  playLength: number;
  /**
   * Cushion nose height above the cloth, metres (absolute, a table property).
   * The contact angle theta for a given ball is derived from this vs its radius,
   * so a larger ball naturally contacts a touch lower on its face.
   */
  cushionNoseHeight: number;
  // ---- render-only dimensions (do not affect physics) ----
  /** Visible width of the wooden rail cap beyond the cushion nose, metres. */
  railWidth: number;
  /** Slate bed thickness, metres. */
  bedThickness: number;
  /** How far the cushion rubber rises above the cloth at the rail, metres. */
  cushionHeight: number;
}

/** Tunable physics coefficients. Defaults below match competition carom cloth. */
export interface PhysicsConstants {
  gravity: number; // m/s^2
  slidingFriction: number; // mu_s, ball-cloth kinetic
  rollingFriction: number; // mu_r, rolling resistance
  spinningFriction: number; // mu_sp, vertical-axis spin decay
  ballBallRestitution: number; // e_bb
  /** Velocity-dependent ball-ball friction fit: a + b*exp(-c*|u_t|). */
  ballBallFriction: { a: number; b: number; c: number };
  cushionRestitution: number; // e_cushion at low speed
  /** Restitution loss per m/s of approach speed (real rails play "deader" when hit hard). */
  cushionRestitutionSpeedFalloff: number;
  /** Floor for the speed-adjusted cushion restitution. */
  cushionRestitutionMin: number;
  cushionFriction: number; // mu_cushion
  /**
   * Cushion roll-grip (0..1): how much the ball "grips" the rail and comes off
   * ROLLING rather than skidding. On rebound, a rolling ball's topspin still
   * points into the rail, so without this it skids a long way until cloth friction
   * converts it. This reorients the into-rail rolling spin toward the rebound:
   * 0 = old behaviour (full skid), 1 = comes off perfectly rolling (no skid).
   * Only touches the vertical-plane rolling spin; side English is unaffected.
   */
  cushionRollGrip: number;
  /**
   * Follow/draw (top/back spin) strength: scales the vertical-tip-offset spin.
   * 1 = the physically "natural" amount (which plays strong); lower tames how
   * much top/back spin a given tip offset imparts. Side English is unaffected.
   */
  followDrawScale: number;
  /**
   * Massé / swerve strength: scales only the forward-tilted (along-travel) part
   * of side spin — the component that curves the ball when the cue is elevated.
   * 1 = full. Lowering it tames swerve on gentle/incidental tilt; a steep,
   * deliberate massé still curves because its response also grows with elevation.
   * Ordinary (vertical-axis) side English — throw and cushion rebound — is
   * unaffected. See `MASSE_TILT_EXPONENT` in physics/strike.ts for the shaping.
   */
  masseScale: number;
  /**
   * Jump strength: fraction of the elevated cue's speed that becomes an upward
   * hop. Lower = balls jump less. The hop also ramps in with elevation (shallow
   * cues can't pop the ball off the cloth), so this scales deliberate jumps.
   */
  jumpFactor: number;
  /** Linear speed (m/s) below which a ball is considered stopped. */
  linearSettle: number;
  /** Angular speed (rad/s) below which spin is considered stopped. */
  angularSettle: number;
}

/**
 * Per-variant table geometry. Korean carom convention:
 * - straight rail is played on the "중대" (middle) table: 1,224 × 2,448 mm inside
 *   the cushions (≈1,500 × 2,720 mm outside), cushion nose 40 mm.
 * - three-cushion uses the international match table: 1,420 × 2,840 mm play
 *   surface, cushion nose 38 mm.
 * The `railWidth` is tuned per variant so the rendered outer frame matches the
 * real outside dimensions; it is render-only and does not affect physics.
 */
export const TABLE_BY_VARIANT: Record<VariantId, TableSpec> = {
  'straight-rail': {
    playWidth: 1.224,
    playLength: 2.448,
    cushionNoseHeight: 0.04,
    railWidth: 0.068, // → outer ≈ 1.5 × 2.728 m
    bedThickness: 0.045,
    cushionHeight: 0.04,
  },
  'three-cushion': {
    playWidth: 1.42,
    playLength: 2.84,
    cushionNoseHeight: 0.038,
    railWidth: 0.05,
    bedThickness: 0.045,
    cushionHeight: 0.038,
  },
};

/** Reference table (three-cushion match table) used where a default is fine. */
export const TABLE: TableSpec = TABLE_BY_VARIANT['three-cushion'];

/** The table geometry for a given variant. */
export function tableFor(variant: VariantId): TableSpec {
  return TABLE_BY_VARIANT[variant];
}

export const PHYSICS: PhysicsConstants = {
  gravity: 9.81,
  slidingFriction: 0.285,
  rollingFriction: 0.025,
  spinningFriction: 0.025,
  ballBallRestitution: 0.96,
  ballBallFriction: { a: 0.01, b: 0.1, c: 1.0 },
  cushionRestitution: 0.85,
  cushionRestitutionSpeedFalloff: 0.02,
  cushionRestitutionMin: 0.6,
  cushionFriction: 0.250,
  cushionRollGrip: 0.7, // ball grips the rail and rolls off instead of skidding (0..1)
  followDrawScale: 0.6, // tamed top/back spin (1 = full natural amount)
  masseScale: 2, // swerve grows with elevation^MASSE_TILT_EXPONENT; lower for less curve
  jumpFactor: 0.18, // upward hop per unit elevated speed (was 0.22; lowered to jump less)
  linearSettle: 1e-3,
  angularSettle: 1e-2,
};

/**
 * Use the continuous-collision solver (exact contact ordering, no penetration)
 * instead of the plain fixed step. Off by default; the fixed step is the
 * well-tested fallback. Flip to true to play with CCD everywhere.
 */
export const USE_CCD = false;

/**
 * Per-variant ball spec, matching Korean carom standards:
 * - straight rail ("4구"): 65.5 mm, 255 g.
 * - three-cushion: standard carom ball, 61.5 mm, 210 g.
 */
export const BALL_BY_VARIANT: Record<VariantId, BallSpec> = {
  'straight-rail': { diameter: 0.0655, mass: 0.255 },
  'three-cushion': { diameter: 0.0615, mass: 0.21 },
};

/** Visual ball colours (sRGB hex). */
export const BALL_COLOR: Record<BallId, number> = {
  white: 0xf2efe6,
  yellow: 0xf2c200,
  red: 0xc81f1f,
  red2: 0xc81f1f, // four-ball's second object ball (identical red)
};

/** Styling shared by every theme (the cloth/wood colour comes from the theme). */
export const STYLE = {
  diamondColor: 0xf0ead2,
  slateColor: 0x0f0f12,
};

export type ThemeId = 'green' | 'blue' | 'red' | 'charcoal';

/** A selectable colour theme: cloth + rail wood + the surrounding hall colours. */
export interface Theme {
  id: ThemeId;
  name: string;
  cloth: number; // felt colour (kept muted so it doesn't glare under the lamps)
  railWood: number; // rail / frame wood
  floor: number; // hall floor
  backdrop: number; // far backdrop tint (dim room)
}

export const THEMES: Record<ThemeId, Theme> = {
  green: { id: 'green', name: 'Tournament Green', cloth: 0x011c00, railWood: 0x190b02, floor: 0x0a0907, backdrop: 0x050706 },
  blue: { id: 'blue', name: 'Tournament Blue', cloth: 0x010524, railWood: 0x140a02, floor: 0x070707, backdrop: 0x040508 },
  red: { id: 'red', name: 'Burgundy', cloth: 0x1c0104, railWood: 0x100502, floor: 0x080505, backdrop: 0x060404 },
  charcoal: { id: 'charcoal', name: 'Charcoal', cloth: 0x050607, railWood: 0x090a0c, floor: 0x060607, backdrop: 0x040405 },
};

export const DEFAULT_THEME: ThemeId = 'green';

/**
 * Standard opening layout. Positions are ball-centre (x, y) on the bed; z is set
 * to the radius at world-build time. The spots scale with the variant's table
 * length; the cue-ball separation is a fixed real-world distance.
 * - three-cushion: red on the foot spot, the two cue balls on the head string.
 * - straight rail (4구): a second red on the centre spot; only it uses `red2`.
 */
export function startPositions(variant: VariantId): Record<BallId, { x: number; y: number }> {
  const quarter = TABLE_BY_VARIANT[variant].playLength / 4;
  return {
    red: { x: 0, y: quarter }, // foot spot
    red2: { x: 0, y: 0 }, // centre spot (four-ball only)
    white: { x: 0, y: -quarter }, // head spot
    yellow: { x: 0.1825, y: -quarter }, // head string, offset
  };
}

export function ballRadius(variant: VariantId): number {
  return BALL_BY_VARIANT[variant].diameter / 2;
}
